import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Mic, MicOff, Send, Volume2, VolumeX, X, MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { apiPost } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

/**
 * Floating "Talk to Owed" assistant. Browser-native Web Speech for STT + TTS
 * (free, zero API cost, works on Chrome / Edge / Safari — including iOS Safari).
 * Falls back to typing if speech APIs aren't available.
 *
 * PRIVACY: Uses the logged-in user's JWT. Server-side prompt is scoped strictly
 * to the authenticated user — never leaks anyone else's data.
 */
export default function VoiceAssistant() {
    const { isAuthed } = useApp();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [listening, setListening] = useState(false);
    const [speaking, setSpeaking] = useState(false);
    const [ttsOn, setTtsOn] = useState(true);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", content: "Hey — I'm Owed. Tap the mic or just type. I can help you pick the right grant, draft an affidavit, or talk through filing. What's on your mind?" },
    ]);
    const recRef = useRef(null);
    const feedRef = useRef(null);

    // Detect browser support once
    const speechSupported = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

    const append = useCallback((m) => setMessages((s) => [...s, m]), []);

    // Auto-scroll message feed
    useEffect(() => {
        if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }, [messages, sending]);

    const speak = useCallback((text) => {
        if (!ttsSupported || !ttsOn || !text) return;
        try {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(text);
            u.rate = 1.02;
            u.pitch = 1;
            u.onstart = () => setSpeaking(true);
            u.onend = () => setSpeaking(false);
            u.onerror = () => setSpeaking(false);
            window.speechSynthesis.speak(u);
        } catch { /* ignore */ }
    }, [ttsSupported, ttsOn]);

    const stopSpeaking = () => {
        if (ttsSupported) window.speechSynthesis.cancel();
        setSpeaking(false);
    };

    const sendMessage = useCallback(async (text) => {
        const trimmed = (text || "").trim();
        if (!trimmed) return;
        append({ role: "user", content: trimmed });
        setInput("");
        setSending(true);
        try {
            const history = messages.slice(-6);
            const r = await apiPost("/assistant/chat", { message: trimmed, page: loc.pathname, history });
            const reply = (r?.reply || "").trim() || "Sorry — I didn't catch that. Try again?";
            append({ role: "assistant", content: reply });
            speak(reply);
        } catch (e) {
            const err = e?.response?.data?.detail || "I'm briefly offline. Try again in a moment.";
            append({ role: "assistant", content: typeof err === "string" ? err : "Couldn't reach the server." });
        } finally {
            setSending(false);
        }
    }, [append, loc.pathname, messages, speak]);

    const startListening = () => {
        if (!speechSupported) return toast.error("Voice isn't supported on this browser — you can type instead.");
        if (listening) return;
        try {
            stopSpeaking();
            const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
            const rec = new Ctor();
            rec.lang = "en-US";
            rec.interimResults = false;
            rec.maxAlternatives = 1;
            rec.continuous = false;
            rec.onstart = () => setListening(true);
            rec.onresult = (ev) => {
                const transcript = ev.results?.[0]?.[0]?.transcript || "";
                if (transcript) sendMessage(transcript);
            };
            rec.onerror = (ev) => {
                setListening(false);
                if (ev.error && ev.error !== "aborted" && ev.error !== "no-speech") {
                    toast.error(`Mic error: ${ev.error}`);
                }
            };
            rec.onend = () => setListening(false);
            recRef.current = rec;
            rec.start();
        } catch (e) {
            setListening(false);
            toast.error("Couldn't start microphone.");
        }
    };

    const stopListening = () => {
        if (recRef.current && listening) {
            try { recRef.current.stop(); } catch { /* ignore */ }
        }
        setListening(false);
    };

    if (!isAuthed) return null;

    return (
        <>
            {/* Floating trigger */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-20 right-4 sm:bottom-6 sm:right-24 z-40 h-14 w-14 rounded-full bg-foreground text-background shadow-elegant hover:scale-105 active:scale-95 transition-all inline-flex items-center justify-center group"
                    data-testid="voice-assistant-open-btn"
                    aria-label="Open Owed assistant"
                >
                    <MessageCircle className="h-6 w-6" />
                    <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-accent border-2 border-background" />
                    <span className="absolute right-full mr-3 whitespace-nowrap rounded-full bg-foreground text-background text-xs px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Talk to Owed</span>
                </button>
            )}

            {/* Panel */}
            {open && (
                <div className="fixed bottom-20 right-4 sm:bottom-6 sm:right-24 z-40 w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-6rem))] rounded-3xl bg-card border border-border shadow-elegant flex flex-col overflow-hidden animate-fade-up" data-testid="voice-assistant-panel">
                    {/* Header */}
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-gradient-warm">
                        <span className="h-9 w-9 rounded-full bg-foreground text-background inline-flex items-center justify-center shrink-0">
                            <MessageCircle className="h-4 w-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-serif text-base leading-tight">Owed</p>
                            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">{listening ? "listening…" : speaking ? "speaking…" : sending ? "thinking…" : "ready"}</p>
                        </div>
                        {ttsSupported && (
                            <button onClick={() => { setTtsOn(s => !s); if (ttsOn) stopSpeaking(); }} className="h-8 w-8 rounded-full hover:bg-secondary inline-flex items-center justify-center text-muted-foreground" data-testid="voice-assistant-tts-toggle" aria-label={ttsOn ? "Mute voice" : "Unmute voice"}>
                                {ttsOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            </button>
                        )}
                        <button onClick={() => { stopListening(); stopSpeaking(); setOpen(false); }} className="h-8 w-8 rounded-full hover:bg-secondary inline-flex items-center justify-center text-muted-foreground" data-testid="voice-assistant-close-btn" aria-label="Close assistant">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Feed */}
                    <div ref={feedRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background">
                        {messages.map((m, i) => (
                            <div key={m.id || `${i}-${m.role}-${(m.content || "").slice(0, 16)}`} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                                <div className={cn(
                                    "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap",
                                    m.role === "user" ? "bg-foreground text-background rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"
                                )}>
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="bg-secondary rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Composer */}
                    <div className="border-t border-border bg-card p-3">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={listening ? stopListening : startListening}
                                disabled={!speechSupported || sending}
                                className={cn(
                                    "h-11 w-11 rounded-full inline-flex items-center justify-center shrink-0 transition-colors",
                                    listening ? "bg-destructive text-destructive-foreground animate-pulse" : "bg-foreground text-background hover:bg-foreground/90",
                                    !speechSupported && "opacity-50 cursor-not-allowed"
                                )}
                                title={speechSupported ? (listening ? "Stop listening" : "Tap to speak") : "Voice not supported in this browser"}
                                data-testid="voice-assistant-mic-btn"
                                aria-label={listening ? "Stop listening" : "Start listening"}
                            >
                                {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                            </button>
                            <Input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                                placeholder={listening ? "Listening…" : "Ask anything…"}
                                disabled={sending}
                                className="h-11 rounded-full bg-secondary/50 border-border text-sm"
                                data-testid="voice-assistant-input"
                            />
                            <button
                                type="button"
                                onClick={() => sendMessage(input)}
                                disabled={sending || !input.trim()}
                                className="h-11 w-11 rounded-full bg-foreground text-background disabled:opacity-40 inline-flex items-center justify-center shrink-0 hover:bg-foreground/90"
                                data-testid="voice-assistant-send-btn"
                                aria-label="Send"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </div>
                        {!speechSupported && (
                            <p className="mt-2 text-[10px] text-muted-foreground">Voice input works best on Chrome, Edge, or Safari.</p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
